/**
 * Day Plan Adherence — analyzer.
 *
 * Reads `journalEntry.plan` (free-text the trader wrote pre-market) and
 * compares it to the setups actually traded. Surfaces:
 *
 *   1. **On-plan-only day** (success) — every trade you took matched a
 *      setup mentioned in your plan. Reinforce: planning works.
 *
 *   2. **Off-plan trades** (warning) — N trades on setups your plan didn't
 *      mention. Names them so you can review whether the deviation was
 *      reactive (FOMO / chop / bad day) or a legitimate adapt.
 *
 *   3. **No plan written** (info) — when the trader has trades for the day
 *      but didn't write a plan. Soft nudge to encourage the habit.
 *
 * Plan parsing is intentionally fuzzy:
 *   - Case-insensitive substring match for each known setup name in the
 *     plan text. "I'll be looking for reversal trades" matches "reversal".
 *   - This is a NLP shortcut, not a real parser. Free-text plans have
 *     too many edge cases (typos, paraphrases, "no breakouts today"
 *     would technically match the setup name but means the opposite).
 *     For v1 we surface counts + names rather than make confident
 *     "you broke your plan" claims; the trader interprets.
 *
 * Reads:
 *   day.trades                       — DayContext-enriched
 *   ctx.journalEntry.plan            — free text (or '')
 *   ctx.knownSetups                  — array of setup names from setupsWithDetails
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';

export const key = 'dayPlanAdherence';
export const tier = 'free';

const MIN_TRADES_FOR_ADHERENCE = 2;
const MIN_TRADES_FOR_NO_PLAN_NUDGE = 3;

/**
 * Extract setup names that appear (case-insensitive substring) in the plan.
 * Returns the matched setups as a Set (canonical case from knownSetups).
 */
export const extractMentionedSetups = (planText, knownSetups) => {
  const out = new Set();
  if (!planText || typeof planText !== 'string') return out;
  if (!Array.isArray(knownSetups)) return out;
  const lower = planText.toLowerCase();
  for (const setup of knownSetups) {
    if (typeof setup !== 'string' || setup.length === 0) continue;
    // Substring match. We don't word-boundary because traders abbreviate
    // ("MMXM" for "MMXM Setup", "rev" for "reversal" — though the latter
    // would require explicit nicknames). Accept the false-positive risk
    // (e.g., "no reversal trades" matches "reversal") in exchange for
    // catching paraphrases and abbreviations.
    if (lower.includes(setup.toLowerCase())) {
      out.add(setup);
    }
  }
  return out;
};

const isNonEmptyPlan = (plan) => {
  if (typeof plan !== 'string') return false;
  // Strip whitespace + obvious placeholder text
  const trimmed = plan.replace(/\s+/g, ' ').trim();
  return trimmed.length >= 10;  // arbitrary "is this a real plan" floor
};

export const analyze = (ctx) => {
  const { day, journalEntry, knownSetups, allTrades } = ctx || {};
  if (!day) return null;
  const trades = Array.isArray(day.trades) && day.trades.length > 0 ? day.trades : day.enriched;
  if (!Array.isArray(trades) || trades.length === 0) return null;

  // Setup-name vocabulary for plan parsing. Prefer the caller-supplied list
  // (the "official" setup names from setupsWithDetails), fall back to the
  // unique setups across all trades. The fallback gives us SOMETHING to
  // match against even when the caller forgets to pass knownSetups.
  let setupsVocab = Array.isArray(knownSetups) && knownSetups.length > 0
    ? knownSetups
    : null;
  if (!setupsVocab) {
    const seen = new Set();
    const corpus = Array.isArray(allTrades) ? allTrades : trades;
    for (const t of corpus) {
      if (t?.setup) seen.add(t.setup);
    }
    setupsVocab = [...seen];
  }

  // Setups actually traded today
  const tradedSetups = new Set();
  for (const t of trades) {
    if (t?.setup) tradedSetups.add(t.setup);
  }
  if (tradedSetups.size === 0) return null;

  const planText = journalEntry?.plan || '';
  const hasPlan = isNonEmptyPlan(planText);

  // ── No-plan nudge ────────────────────────────────────────────────────
  if (!hasPlan && trades.length >= MIN_TRADES_FOR_NO_PLAN_NUDGE) {
    return makeObservation({
      id: `${key}:${day.date || ''}:no-plan`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date || '',
      severity: SEVERITY.INFO,
      category: CATEGORY.PROCESS,
      title: `Today had ${trades.length} trades but no pre-market plan`,
      detail: `Without a written plan, Coach can't tell whether today's trades were aligned with your intent or reactive to the tape. Even a 2-3 sentence "I'll focus on X, avoid Y" gives Coach + your future self something to compare against.`,
      action: `Tomorrow morning, write 2-3 sentences in the Plan field before the first trade. It only takes a minute and unlocks the adherence analyzer.`,
      evidence: { tradeCount: trades.length, tradedSetups: [...tradedSetups] },
      confidence: 0.85,
      priority: 50,
    });
  }
  // No plan AND not enough trades for a nudge — silent
  if (!hasPlan) return null;
  // Plan exists but not enough trades to evaluate adherence
  if (trades.length < MIN_TRADES_FOR_ADHERENCE) return null;

  // Compute adherence using the resolved setupsVocab (either caller-
  // supplied or derived from allTrades).
  const mentioned = extractMentionedSetups(planText, setupsVocab);
  const onPlan = [];
  const offPlan = [];
  for (const t of trades) {
    if (!t?.setup) continue;
    if (mentioned.has(t.setup)) onPlan.push(t);
    else offPlan.push(t);
  }

  const offPlanSetups = [...new Set(offPlan.map(t => t.setup))];

  // ── Fully on-plan day (success) ──────────────────────────────────────
  if (offPlan.length === 0 && onPlan.length > 0) {
    return makeObservation({
      id: `${key}:${day.date || ''}:on-plan`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date || '',
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.DISCIPLINE,
      title: `Every trade today matched your written plan`,
      detail: `${onPlan.length} of ${trades.length} trades on setups you mentioned: ${[...mentioned].join(', ')}. Discipline win — pre-market intent held through the session.`,
      action: `Mark today as a process-A. The P&L outcome is secondary; the adherence is the lever you control.`,
      evidence: {
        tradeCount: trades.length, onPlanCount: onPlan.length,
        mentionedSetups: [...mentioned],
      },
      confidence: 0.85,
      priority: 70,
    });
  }

  // ── Off-plan trades (warning) ────────────────────────────────────────
  if (offPlan.length > 0) {
    const onPlanShare = trades.length > 0 ? onPlan.length / trades.length : 0;
    return makeObservation({
      id: `${key}:${day.date || ''}:off-plan`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date || '',
      severity: offPlan.length > onPlan.length ? SEVERITY.WARNING : SEVERITY.INFO,
      category: CATEGORY.DISCIPLINE,
      title: `${offPlan.length} of ${trades.length} trades today were OFF-PLAN`,
      detail: mentioned.size > 0
        ? `Your plan mentioned: ${[...mentioned].join(', ')}. You also traded: ${offPlanSetups.join(', ')}. Was the deviation reactive (chase / FOMO) or a legitimate adapt to what you saw?`
        : `Your plan didn't mention any of today's traded setups (${offPlanSetups.join(', ')}). Either the plan was too vague to match, or you completely deviated from it.`,
      action: offPlan.length > onPlan.length
        ? `Most of today was off-plan. Tomorrow's plan needs to either be more specific OR you need to actively check it before each entry.`
        : `Mostly on-plan with some adapts — review the off-plan trades. Were they your best or your worst?`,
      evidence: {
        tradeCount: trades.length,
        onPlanCount: onPlan.length,
        offPlanCount: offPlan.length,
        onPlanShare,
        mentionedSetups: [...mentioned],
        offPlanSetups,
      },
      confidence: 0.7,  // lower because plan parsing is fuzzy
      priority: 72,
    });
  }
  return null;
};

// Test-friendly exports
export {
  isNonEmptyPlan,
  MIN_TRADES_FOR_ADHERENCE,
  MIN_TRADES_FOR_NO_PLAN_NUDGE,
};
